import { type LucideIcon, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { cn } from '@nepal-ecom/ui';
import { Card, CardContent } from '@nepal-ecom/ui';

export type StatTone = 'primary' | 'emerald' | 'amber' | 'red' | 'cobalt' | 'purple' | 'secondary';

const TONES: Record<StatTone, { iconBg: string; icon: string; ring: string }> = {
  primary: { iconBg: 'bg-primary-50', icon: 'text-primary-600', ring: 'ring-primary-100' },
  emerald: { iconBg: 'bg-emerald-50', icon: 'text-emerald-600', ring: 'ring-emerald-100' },
  amber: { iconBg: 'bg-amber-50', icon: 'text-amber-600', ring: 'ring-amber-100' },
  red: { iconBg: 'bg-red-50', icon: 'text-red-600', ring: 'ring-red-100' },
  cobalt: { iconBg: 'bg-cobalt-50', icon: 'text-cobalt-600', ring: 'ring-cobalt-100' },
  purple: { iconBg: 'bg-purple-50', icon: 'text-purple-600', ring: 'ring-purple-100' },
  secondary: { iconBg: 'bg-secondary-50', icon: 'text-secondary-600', ring: 'ring-secondary-100' },
};

interface StatCardProps {
  icon: LucideIcon;
  title: string;
  value: string;
  trend?: number;
  description?: string;
  tone?: StatTone;
  className?: string;
}

export function StatCard({
  icon: Icon,
  title,
  value,
  trend,
  description,
  tone = 'primary',
  className,
}: StatCardProps) {
  const t = TONES[tone];
  const hasTrend = trend !== undefined;
  const isPositive = (trend ?? 0) > 0;
  const isNeutral = (trend ?? 0) === 0;
  const abs = Math.abs(trend ?? 0);

  let TrendIcon = Minus;
  let trendTone = 'text-slate-400';
  if (!isNeutral) {
    TrendIcon = isPositive ? TrendingUp : TrendingDown;
    trendTone = isPositive ? 'text-emerald-600' : 'text-red-500';
  }

  return (
    <Card className={cn('transition-all hover:shadow-card-hover hover:-translate-y-0.5', className)}>
      <CardContent className="p-5 flex items-start gap-4">
        <div className={cn('h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0 ring-1', t.iconBg, t.ring)}>
          <Icon className={cn('h-[18px] w-[18px]', t.icon)} />
        </div>
        <div className="flex-1 min-w-0 space-y-0.5">
          <p className="text-[11px] font-semibold text-slate-500 uppercase tracking-wider truncate">{title}</p>
          <p className="text-2xl font-bold text-slate-900 tabular-nums truncate">{value}</p>
          {hasTrend && (
            <div className={cn('inline-flex items-center gap-1 text-xs font-semibold mt-1', trendTone)}>
              <TrendIcon className="h-3 w-3" />
              {isNeutral ? 'No change' : `${abs.toFixed(1)}% vs last period`}
            </div>
          )}
          {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
        </div>
      </CardContent>
    </Card>
  );
}
